import { cn } from '@nexo/ui';
import {
  ArrowRight,
  AtSign,
  BellRing,
  CalendarClock,
  Check,
  Download,
  FileLock2,
  Globe,
  Hand,
  Hash,
  Laptop,
  ListTodo,
  MessagesSquare,
  Mic,
  Monitor,
  ShieldCheck,
  Terminal,
  Video,
  X,
} from 'lucide-react';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router';
import { AppVersion } from '../../components/AppVersion';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { Logo, LogoMark } from '../../components/Logo';
import { currentLanguage } from '../../lib/i18n';
import { isDesktop } from '../../lib/platform';
import { useAuth } from '../auth/AuthProvider';
import {
  detectOs,
  osLabels,
  PlatformDownloads,
  RecommendedDownload,
  recommendedAsset,
  useLatestRelease,
} from '../download/DownloadOptions';
import { PriorityIcon, StatusIcon } from '../tasks/TaskIcons';
import { LiveDemo } from './LiveDemo';

// Launch on Product Hunt (post 1270802): official badge, and a welcome for visitors coming from it.
const PRODUCT_HUNT_URL =
  'https://www.producthunt.com/products/nexo-5?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-nexo-6';
const PRODUCT_HUNT_BADGE =
  'https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1270802&theme=dark';
const fromProductHunt =
  typeof window !== 'undefined' &&
  (/[?&](ref|utm_source)=producthunt\b/i.test(window.location.search) ||
    document.referrer.includes('producthunt.com'));

/** Pill buttons of the landing (always on the dark sky). */
const pill = {
  solid:
    'inline-flex h-11 items-center gap-2 rounded-full bg-white px-5 text-[15px] font-semibold text-[oklch(0.18_0.04_268)] shadow-[0_8px_30px_-6px_oklch(0.8_0.15_70/0.45)] transition hover:-translate-y-0.5 hover:bg-white/90',
  outline:
    'inline-flex h-11 items-center gap-2 rounded-full border border-white/25 px-5 text-[15px] font-medium text-white backdrop-blur transition hover:border-white/50 hover:bg-white/5',
};

const moreFeatures: { icon: ReactNode; title: string; text: string }[] = [
  {
    icon: <CalendarClock />,
    title: 'Reuniones con invitados',
    text: 'Programa reuniones y comparte un enlace: los externos entran sin crearse una cuenta.',
  },
  {
    icon: <BellRing />,
    title: 'Avisos a tu medida',
    text: '«No molestar», horario sin avisos, estado personalizado y un resumen diario por email.',
  },
  {
    icon: <Monitor />,
    title: 'Web y escritorio',
    text: 'En el navegador o con la app para Windows, macOS y Linux, que se actualiza sola.',
  },
];

const facts = [
  { value: '2–20', label: 'personas por equipo', note: 'Pensado para equipos pequeños' },
  { value: '4', label: 'plataformas', note: 'Windows, macOS, Linux y web' },
  { value: '1', label: 'clave por archivo', note: 'Cada archivo, cifrado por separado' },
  { value: '0 €', label: 'para empezar', note: 'Sin tarjeta' },
];

const security: { icon: ReactNode; title: string; text: string }[] = [
  {
    icon: <FileLock2 />,
    title: 'Archivos cifrados',
    text: 'Cada archivo se guarda cifrado con su propia clave; ni el nombre queda a la vista.',
  },
  {
    icon: <ShieldCheck />,
    title: 'Tus sesiones, bajo control',
    text: 'Mira desde qué dispositivos has entrado y cierra cualquiera al instante.',
  },
  {
    icon: <AtSign />,
    title: 'Roles y permisos por canal',
    text: 'Decide quién ve, escribe o gestiona cada canal, con roles a medida.',
  },
];

export function LandingPage() {
  const { t } = useTranslation();
  // The promo video has a Spanish and an English cut (useTranslation re-renders on ES/EN switch).
  const demoSuffix = currentLanguage() === 'es' ? '-es' : '';
  const { status } = useAuth();
  const latest = useLatestRelease();
  if (status === 'authenticated') return <Navigate to="/app" replace />;
  // The desktop app has no marketing page: go straight to sign in.
  if (isDesktop && status === 'anonymous') return <Navigate to="/login" replace />;

  const os = detectOs();
  const release = latest.data;
  const recommended = recommendedAsset(release, os);

  return (
    // The landing is always the night sky, whatever theme the visitor uses in the app.
    <div className="dark relative min-h-full overflow-x-hidden bg-[oklch(0.11_0.02_268)] text-text">
      {/* ---------- Hero ---------- */}
      <div className="relative isolate overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <div className="nx-stars absolute inset-0" />
          <div className="nx-stars nx-stars-2 absolute inset-0" />
          <div className="absolute inset-x-0 bottom-0 h-[60%] overflow-hidden">
            <div className="nx-grid absolute inset-x-0 bottom-0 h-full" />
          </div>
          <div className="nx-aurora absolute inset-x-[-10%] bottom-[-34%] h-[80%]" />
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-[oklch(0.11_0.02_268)]" />
        </div>

        <header className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-5 py-5">
          <Link to="/" aria-label="Nexo">
            <Logo className="text-white" />
          </Link>
          <nav className="flex items-center gap-1 text-sm text-white/65 max-md:hidden">
            {[
              ['#funciones', 'Funciones'],
              ['#seguridad', 'Seguridad'],
              ['#descargar', 'Descargar'],
            ].map(([href, label]) => (
              <a
                key={href}
                href={href}
                className="rounded-full px-3 py-1.5 transition hover:bg-white/5 hover:text-white"
              >
                {t(label ?? '')}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact className="text-white/80 hover:text-white" />
            <Link
              to="/login"
              className="rounded-full px-3 py-2 text-sm font-medium text-white/80 hover:text-white"
            >
              {t('Entrar')}
            </Link>
            <Link to="/register" className={cn(pill.outline, 'h-9 px-4 text-sm')}>
              {t('Empezar gratis')}
            </Link>
          </div>
        </header>

        <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 pt-10 pb-20 sm:pt-16 lg:grid-cols-[1.05fr_1fr] lg:pb-28">
          <div>
            <a
              href={fromProductHunt ? '/register' : '#descargar'}
              className="animate-rise inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-white/75 backdrop-blur hover:text-white"
            >
              <span className="relative flex size-1.5">
                <span className="nx-pulse-ring absolute inset-0 rounded-full bg-glow" />
                <span className="relative size-1.5 rounded-full bg-glow" />
              </span>
              {fromProductHunt
                ? t('👋 ¡Hola, Product Hunters! Nexo es gratis')
                : release
                  ? t('Nexo {{version}} ya disponible', { version: release.version })
                  : t('Chat, tareas y llamadas')}
              <ArrowRight className="size-3" />
            </a>
            <h1
              className="animate-rise mt-6 font-display text-[2.6rem] leading-[1.05] font-semibold tracking-tight text-balance text-white sm:text-6xl lg:text-[4.2rem]"
              style={{ animationDelay: '80ms' }}
            >
              <Trans
                i18nKey="Donde tu equipo habla, decide <grad>y avanza</grad>"
                components={{
                  grad: (
                    <span className="bg-gradient-to-r from-[oklch(0.78_0.13_255)] via-[oklch(0.85_0.12_75)] to-[oklch(0.78_0.16_55)] bg-clip-text text-transparent" />
                  ),
                }}
              />
            </h1>
            <p
              className="animate-rise mt-6 max-w-lg text-lg text-pretty text-white/70"
              style={{ animationDelay: '160ms' }}
            >
              {t(
                'Chat, tareas, llamadas y reuniones en un solo sitio, para equipos de 2 a 20 personas que quieren menos herramientas y más foco.',
              )}
            </p>
            <div
              className="animate-rise mt-9 flex flex-wrap items-center gap-3"
              style={{ animationDelay: '240ms' }}
            >
              <Link to="/register" className={pill.solid}>
                {t('Crear mi equipo')} <ArrowRight className="size-4" />
              </Link>
              {recommended && os ? (
                <RecommendedDownload asset={recommended} os={os} variant="secondary" />
              ) : (
                <a href="#descargar" className={pill.outline}>
                  <Download className="size-4" /> {t('Descargar la app')}
                </a>
              )}
            </div>
            <div
              className="animate-rise mt-10 flex flex-wrap items-center gap-4 text-sm text-white/50"
              style={{ animationDelay: '320ms' }}
            >
              <span>{t('Gratis · Sin tarjeta')}</span>
              <span aria-hidden className="h-4 w-px bg-white/15" />
              <span className="flex items-center gap-2" title={t('Windows, macOS, Linux y web')}>
                {[Monitor, Laptop, Terminal, Globe].map((Icon, i) => (
                  <span
                    key={i}
                    className="flex size-8 items-center justify-center rounded-full border border-white/12 bg-white/5"
                  >
                    <Icon className="size-3.5" />
                  </span>
                ))}
              </span>
            </div>
          </div>

          <NetworkHero />
        </section>
      </div>

      <main className="relative mx-auto max-w-6xl px-5">
        {/* ---------- Features: the warm panel of the reference ---------- */}
        <Reveal>
          <section
            id="funciones"
            className="relative scroll-mt-6 overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,oklch(0.42_0.08_68/0.9)_0%,oklch(0.28_0.05_250/0.95)_45%,oklch(0.16_0.03_265)_100%)] px-5 py-14 sm:px-10 sm:py-20"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -top-40 left-1/2 size-[36rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,oklch(0.85_0.14_75/0.35),transparent_65%)]"
            />
            <SectionTitle
              eyebrow={t('Funciones')}
              title={t('Todo lo que tu equipo usa cada día')}
              text={t(
                'Sin saltar entre cinco aplicaciones: la conversación, el trabajo y las llamadas, juntos.',
              )}
            />
            <div className="relative mt-12 grid gap-5 lg:grid-cols-3">
              <FeatureCard
                delay={0}
                art={<ChatArt />}
                icon={<MessagesSquare />}
                title={t('Chat que no se pierde')}
                text={t(
                  'Canales, mensajes directos, hilos, reacciones, respuestas citadas y mensajes fijados.',
                )}
              />
              <FeatureCard
                delay={120}
                art={<TaskArt />}
                icon={<ListTodo />}
                title={t('Tareas junto a la conversación')}
                text={t(
                  'Convierte un mensaje en tarea con responsable, prioridad, fecha, checklist y sprints.',
                )}
              />
              <FeatureCard
                delay={240}
                art={<CallArt />}
                icon={<Video />}
                title={t('Llamadas y videollamadas')}
                text={t(
                  'Entra con un clic, comparte pantalla, levanta la mano y chatea sin salir de la llamada.',
                )}
              />
            </div>
            <div className="relative mt-5 grid gap-5 sm:grid-cols-3">
              {moreFeatures.map((feature, i) => (
                <Reveal key={feature.title} delay={i * 100}>
                  <div className="hover-lift h-full rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur">
                    <IconBadge>{feature.icon}</IconBadge>
                    <h3 className="mt-3 font-display text-[15px] font-semibold text-white">
                      {t(feature.title)}
                    </h3>
                    <p className="mt-1 text-sm text-white/60">{t(feature.text)}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ---------- Product preview ---------- */}
        <section className="pt-24">
          <Reveal>
            <p className="text-center font-display text-lg font-medium text-white/80">
              {t('Así se ve un día normal en Nexo')}
            </p>
          </Reveal>
          <Reveal delay={100}>
            <LiveDemo />
          </Reveal>
        </section>

        {/* ---------- Promo video (self-hosted; nothing is downloaded until play) ---------- */}
        <section className="pt-24">
          <Reveal>
            <p className="text-center font-display text-lg font-medium text-white/80">
              {t('Nexo en 45 segundos')}
            </p>
          </Reveal>
          <Reveal delay={100}>
            <video
              key={demoSuffix}
              className="mx-auto mt-8 w-full max-w-5xl rounded-[1.4rem] border border-white/12 bg-black shadow-[0_40px_120px_-30px_oklch(0.75_0.16_60/0.35)]"
              src={`/media/nexo-demo${demoSuffix}.mp4`}
              poster={`/media/nexo-demo-poster${demoSuffix}.jpg`}
              controls
              playsInline
              preload="none"
              aria-label={t('Vídeo de presentación de Nexo')}
            />
          </Reveal>
        </section>

        {/* ---------- Facts (no invented metrics: only what is true of the product) ---------- */}
        <section className="pt-24">
          <Reveal>
            <p className="text-sm font-medium text-white/50">{t('Nexo en cifras')}</p>
          </Reveal>
          <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4">
            {facts.map((fact, i) => (
              <Reveal key={fact.label} delay={i * 90}>
                <p className="text-sm text-white/60">{t(fact.label)}</p>
                <p className="mt-1 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                  {t(fact.value)}
                </p>
                <p className="mt-2 flex items-center gap-1.5 text-xs text-glow">
                  <Check className="size-3.5" /> {t(fact.note)}
                </p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ---------- Security ---------- */}
        <section id="seguridad" className="scroll-mt-6 pt-28">
          <Reveal>
            <p className="text-sm font-medium text-white/50">{t('Privacidad')}</p>
            <h2 className="mt-2 max-w-2xl font-display text-3xl font-semibold tracking-tight text-balance text-white sm:text-[2.6rem] sm:leading-tight">
              {t('Lo de tu equipo se queda en tu equipo')}
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {security.map((item, i) => (
              <Reveal key={item.title} delay={i * 110}>
                <div className="hover-lift h-full rounded-2xl border border-white/10 bg-[linear-gradient(160deg,oklch(0.22_0.04_265),oklch(0.15_0.025_268))] p-5">
                  <div className="flex items-center gap-3">
                    <IconBadge>{item.icon}</IconBadge>
                    <h3 className="font-display text-[15px] font-semibold text-white">
                      {t(item.title)}
                    </h3>
                  </div>
                  <p className="mt-3 text-sm text-white/60">{t(item.text)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ---------- Download ---------- */}
        <section id="descargar" className="scroll-mt-6 pt-28">
          <Reveal>
            <SectionTitle
              eyebrow={t('Descargar')}
              title={t('Nexo en tu escritorio')}
              text={
                release
                  ? t('Versión {{version}} para Windows, macOS y Linux. Se actualiza sola.', {
                      version: release.version,
                    })
                  : t('La app para Windows, macOS y Linux llega muy pronto.')
              }
            />
          </Reveal>
          {recommended && os && (
            <Reveal delay={80}>
              <div className="mt-8 flex justify-center">
                <RecommendedDownload asset={recommended} os={os} />
              </div>
            </Reveal>
          )}
          <Reveal delay={140}>
            <div className="mt-8">
              <PlatformDownloads release={release} os={os} />
            </div>
          </Reveal>
          <p className="mt-4 text-center text-sm text-white/55">
            {t('¿Otro sistema o versión anterior?')}{' '}
            <Link to="/download" className="font-medium text-accent hover:underline">
              {t('Ver la página de descargas')}
            </Link>
            {os &&
              !recommended &&
              release &&
              ` · ${t('aún no hay app para {{os}}', { os: osLabels[os] })}`}
          </p>
        </section>

        {/* ---------- Closing call to action over the planet's horizon ---------- */}
        <section className="py-28">
          <Reveal>
            <div className="relative isolate overflow-hidden rounded-[2rem] border border-white/10 bg-[oklch(0.13_0.025_268)] px-6 py-16 sm:px-12">
              <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
                <div className="nx-stars absolute inset-0 opacity-60" />
                <div className="absolute top-[68%] left-1/2 aspect-square w-[170%] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_50%_0%,oklch(0.38_0.1_255),oklch(0.14_0.03_265)_35%)] shadow-[0_-2px_0_oklch(0.9_0.1_75/0.85),0_-40px_120px_-10px_oklch(0.75_0.16_60/0.6)]" />
              </div>
              <div className="grid items-center gap-10 md:grid-cols-[1fr_auto]">
                <div>
                  <h2 className="font-display text-3xl font-semibold tracking-tight text-balance text-white sm:text-5xl">
                    {t('Monta tu equipo en un minuto')}
                  </h2>
                  <p className="mt-4 max-w-md text-white/65">
                    {t(
                      'Crea el equipo, invita con un enlace y empieza a hablar. Gratis y sin tarjeta.',
                    )}
                  </p>
                  <Link to="/register" className={cn(pill.solid, 'mt-8')}>
                    {t('Empezar gratis')} <ArrowRight className="size-4" />
                  </Link>
                </div>
                <div className="nx-float relative mx-auto max-md:hidden">
                  <div className="absolute inset-0 rounded-[2.5rem] bg-glow/30 blur-3xl" />
                  <LogoMark className="relative size-40 drop-shadow-2xl" />
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <ProductHuntBadge />

      <footer className="relative border-t border-white/10">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 text-sm sm:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div>
            <Logo className="text-white" />
            <p className="mt-3 max-w-xs text-white/50">
              {t('Chat, tareas y llamadas para equipos pequeños.')}
            </p>
          </div>
          <FooterColumn title={t('Producto')}>
            <a href="#funciones">{t('Funciones')}</a>
            <a href="#seguridad">{t('Seguridad')}</a>
            <Link to="/download">{t('Descargas')}</Link>
          </FooterColumn>
          <FooterColumn title={t('Cuenta')}>
            <Link to="/login">{t('Entrar')}</Link>
            <Link to="/register">{t('Crear cuenta')}</Link>
          </FooterColumn>
          <FooterColumn title={t('Legal')}>
            <Link to="/privacy">{t('Política de privacidad')}</Link>
            <Link to="/terms">{t('Términos de uso')}</Link>
            <Link to="/legal">{t('Aviso legal')}</Link>
          </FooterColumn>
        </div>
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 pb-8 text-xs text-white/40">
          <p>
            © {new Date().getFullYear()} Nexo · {t('Hecho para equipos pequeños')}
          </p>
          <AppVersion />
        </div>
      </footer>
    </div>
  );
}

/** Fades its content up the first time it scrolls into view. */
function Reveal({ children, delay = 0 }: { children: ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(true);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    // Only hide what is still below the fold: no flash for what the visitor already sees.
    if (node.getBoundingClientRect().top < window.innerHeight * 0.9) return;
    setShown(false);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShown(true);
        observer.disconnect();
      },
      { rootMargin: '0px 0px -10% 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-shown={shown}
      className="nx-reveal"
      style={{ '--nx-delay': `${delay}ms` } as CSSProperties}
    >
      {children}
    </div>
  );
}

function SectionTitle({ eyebrow, title, text }: { eyebrow: string; title: string; text: string }) {
  return (
    <div className="relative mx-auto max-w-2xl text-center">
      <p className="text-sm font-medium text-white/70">{eyebrow}</p>
      <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-balance text-white sm:text-[2.6rem] sm:leading-tight">
        {title}
      </h2>
      <p className="mt-3 text-pretty text-white/65">{text}</p>
    </div>
  );
}

function IconBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.07] text-glow [&_svg]:size-[18px]">
      {children}
    </span>
  );
}

function FeatureCard({
  art,
  icon,
  title,
  text,
  delay,
}: {
  art: ReactNode;
  icon: ReactNode;
  title: string;
  text: string;
  delay: number;
}) {
  return (
    <Reveal delay={delay}>
      <article className="group hover-lift h-full overflow-hidden rounded-3xl border border-white/10 bg-[oklch(0.15_0.025_268/0.75)] backdrop-blur">
        <div
          aria-hidden
          className="relative h-48 overflow-hidden border-b border-white/10 bg-[radial-gradient(120%_90%_at_50%_0%,oklch(0.32_0.08_258),oklch(0.16_0.03_266))] p-4"
        >
          {art}
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3">
            <IconBadge>{icon}</IconBadge>
            <h3 className="font-display text-[17px] font-semibold text-white">{title}</h3>
          </div>
          <p className="mt-3 text-[15px] text-white/65">{text}</p>
        </div>
      </article>
    </Reveal>
  );
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold tracking-[0.25em] text-white/40 uppercase">{title}</p>
      <nav className="mt-3 flex flex-col gap-2 text-white/65 [&>a:hover]:text-white">
        {children}
      </nav>
    </div>
  );
}

/* ---------- Illustrations: built from the app's own pieces, never screenshots ---------- */

const avatarTones = [
  'from-[oklch(0.75_0.14_255)] to-[oklch(0.55_0.2_268)]',
  'from-[oklch(0.85_0.13_75)] to-[oklch(0.68_0.17_45)]',
  'from-[oklch(0.8_0.12_165)] to-[oklch(0.6_0.14_200)]',
  'from-[oklch(0.78_0.14_330)] to-[oklch(0.58_0.2_300)]',
];

function Avatar({ name, tone, className }: { name: string; tone: number; className?: string }) {
  return (
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-semibold text-white ring-2 ring-[oklch(0.15_0.025_268)]',
        avatarTones[tone % avatarTones.length],
        className,
      )}
    >
      {name[0]}
    </span>
  );
}

/** Hero illustration: the team as a network of cards wired to Nexo in the middle. */
function NetworkHero() {
  const { t } = useTranslation();
  // Card anchors, in % of the box; the lines go from the hub to them.
  const hub = { x: 50, y: 50 };
  const nodes = [
    { x: 74, y: 13 },
    { x: 90, y: 62 },
    { x: 20, y: 86 },
    { x: 12, y: 30 },
  ];
  return (
    <div aria-hidden className="relative mx-auto h-[26rem] w-full max-w-lg max-lg:hidden">
      <svg className="absolute inset-0 size-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id="nx-link" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="oklch(0.75 0.15 258)" />
            <stop offset="1" stopColor="oklch(0.85 0.14 70)" />
          </linearGradient>
        </defs>
        {nodes.map((node, i) => (
          <g key={i}>
            <line
              x1={hub.x}
              y1={hub.y}
              x2={node.x}
              y2={node.y}
              stroke="oklch(1 0 0 / 0.12)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1={hub.x}
              y1={hub.y}
              x2={node.x}
              y2={node.y}
              stroke="url(#nx-link)"
              strokeWidth="1.5"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              className="nx-flow"
              style={{ animationDelay: `${i * -0.4}s` }}
            />
          </g>
        ))}
      </svg>
      {nodes.map((node, i) => (
        <span
          key={i}
          className="absolute size-2.5 -translate-1/2 rounded-full bg-white shadow-[0_0_12px_2px_oklch(0.85_0.14_70/0.7)]"
          style={{ left: `${node.x}%`, top: `${node.y}%` }}
        />
      ))}

      {/* Hub */}
      <div className="absolute -translate-1/2" style={{ left: `${hub.x}%`, top: `${hub.y}%` }}>
        <span className="nx-pulse-ring absolute inset-0 rounded-[1.4rem] bg-glow/40" />
        <LogoMark className="relative size-16 drop-shadow-[0_10px_30px_oklch(0.75_0.16_60/0.5)]" />
      </div>

      <HeroCard className="top-[2%] right-[-4%] w-64" delay="0s">
        <div className="flex items-center gap-2.5">
          <span className="flex size-9 items-center justify-center rounded-xl bg-white/10">
            <Hash className="size-4 text-white/80" />
          </span>
          <div>
            <p className="text-sm font-semibold text-white">{t('lanzamiento')}</p>
            <p className="text-xs text-white/55">{t('Estudio Alba · 6 personas')}</p>
          </div>
        </div>
        <p className="mt-3 text-[13px] leading-snug text-white/70">
          {t('«¿Quién prepara la nota de prensa?» — Lucía')}
        </p>
        <p className="mt-2 flex items-center gap-1 text-[11px] text-white/45">
          <span className="flex gap-0.5">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className="size-1 animate-bounce rounded-full bg-white/60"
                style={{ animationDelay: `${dot * 120}ms` }}
              />
            ))}
          </span>
          {t('Marcos está escribiendo')}
        </p>
      </HeroCard>

      <HeroCard className="top-[52%] right-[-10%] w-48" delay="-2s">
        <p className="flex items-center gap-1.5 text-xs font-medium text-success">
          <span className="size-1.5 animate-pulse rounded-full bg-success" />{' '}
          {t('Llamada en curso')}
        </p>
        <div className="mt-2.5 flex -space-x-2">
          {['Irene', 'Marcos', 'Lucía', 'Pablo'].map((name, i) => (
            <Avatar key={name} name={name} tone={i} className="size-7" />
          ))}
        </div>
        <p className="mt-2 text-xs text-white/55">{t('Diseño · 12:40')}</p>
      </HeroCard>

      <HeroCard className="bottom-[-2%] left-[-6%] w-56" delay="-4s">
        <p className="text-[11px] font-semibold tracking-wider text-white/45 uppercase">
          {t('Tarea creada')}
        </p>
        <p className="mt-1.5 flex items-center gap-2 text-sm font-medium text-white">
          <StatusIcon status="in_progress" /> {t('Nota de prensa')}
        </p>
        <p className="mt-2 flex items-center gap-2 text-xs text-white/55">
          <Avatar name="Marcos" tone={1} className="size-5 text-[9px] ring-0" /> Marcos
          <span className="ml-auto rounded-md bg-highlight/20 px-1.5 py-0.5 text-highlight">
            {t('Jueves')}
          </span>
        </p>
      </HeroCard>

      <HeroCard className="top-[20%] left-[-10%] w-40" delay="-1s">
        <div className="flex items-center gap-2">
          <Avatar name="Irene" tone={3} />
          <div>
            <p className="text-sm font-semibold text-white">Irene</p>
            <p className="flex items-center gap-1 text-[11px] text-success">
              <span className="size-1.5 rounded-full bg-success" /> {t('Disponible')}
            </p>
          </div>
        </div>
      </HeroCard>
    </div>
  );
}

function HeroCard({
  className,
  delay,
  children,
}: {
  className: string;
  delay: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'nx-float absolute rounded-2xl border border-white/12 bg-[oklch(0.17_0.03_266/0.8)] p-3.5 shadow-[0_20px_50px_-15px_black] backdrop-blur-md',
        className,
      )}
      style={{ animationDelay: delay }}
    >
      {children}
    </div>
  );
}

function ChatArt() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col justify-end gap-2.5">
      <div className="flex items-end gap-2 transition duration-500 group-hover:-translate-y-1">
        <Avatar name="Lucía" tone={0} className="size-6 text-[10px]" />
        <p className="rounded-2xl rounded-bl-md bg-white/10 px-3 py-2 text-xs text-white/85">
          {t('¿Revisamos la portada hoy?')}
        </p>
      </div>
      <div className="flex items-end justify-end gap-2 transition delay-75 duration-500 group-hover:-translate-y-1">
        <p className="rounded-2xl rounded-br-md bg-accent px-3 py-2 text-xs text-accent-fg">
          {t('A las 17:00 en #diseño 👍')}
        </p>
      </div>
      <div className="flex gap-1.5 pl-8">
        {['👍 3', '🎉 2', '👀 1'].map((reaction, i) => (
          <span
            key={reaction}
            className="animate-pop rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] text-white/75"
            style={{ animationDelay: `${400 + i * 120}ms` }}
          >
            {reaction}
          </span>
        ))}
      </div>
    </div>
  );
}

function TaskArt() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col justify-center gap-2">
      {[
        { title: 'Revisar contrato', status: 'todo' as const, urgent: true },
        { title: 'Presentación del sprint', status: 'in_progress' as const },
        { title: 'Nota de prensa', status: 'done' as const },
      ].map((task, i) => (
        <div
          key={task.title}
          className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-xs text-white/85 transition duration-500 group-hover:translate-x-1"
          style={{ transitionDelay: `${i * 60}ms` }}
        >
          <StatusIcon status={task.status} />
          {task.urgent && <PriorityIcon priority="urgent" />}
          <span className={task.status === 'done' ? 'text-white/45 line-through' : ''}>
            {t(task.title)}
          </span>
        </div>
      ))}
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
        <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-accent to-glow transition-all duration-700 group-hover:w-5/6" />
      </div>
    </div>
  );
}

function CallArt() {
  return (
    <div className="grid h-full grid-cols-2 gap-2">
      {['Irene', 'Marcos', 'Lucía', 'Pablo'].map((name, i) => (
        <div
          key={name}
          className={cn(
            'relative flex items-center justify-center rounded-xl border bg-white/[0.05]',
            i === 1
              ? 'border-success/70 shadow-[0_0_0_3px_oklch(0.74_0.14_155/0.2)]'
              : 'border-white/10',
          )}
        >
          <Avatar name={name} tone={i} className="size-9" />
          <span className="absolute bottom-1.5 left-2 flex items-center gap-1 text-[10px] text-white/70">
            {i === 1 ? <Mic className="size-2.5 text-success" /> : null}
            {name}
          </span>
          {i === 2 && (
            <Hand className="absolute top-1.5 right-1.5 size-3.5 animate-bounce text-glow" />
          )}
        </div>
      ))}
    </div>
  );
}

const BADGE_DISMISSED_KEY = 'nexo.productHuntBadge.dismissed';

/** Floating Product Hunt badge in the corner; slides in after a moment and can be closed. */
function ProductHuntBadge() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(BADGE_DISMISSED_KEY)) return;
    } catch {
      // Storage unavailable: show it anyway.
    }
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;
  return (
    <div className="animate-rise fixed right-4 bottom-4 z-50 sm:right-6 sm:bottom-6">
      <a
        href={PRODUCT_HUNT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-xl shadow-[0_20px_50px_-12px_black] transition hover:-translate-y-0.5"
      >
        <img
          src={PRODUCT_HUNT_BADGE}
          alt={t('Nexo en Product Hunt')}
          width={250}
          height={54}
          className="max-sm:w-[200px]"
        />
      </a>
      <button
        type="button"
        aria-label={t('Cerrar')}
        onClick={() => {
          setVisible(false);
          try {
            localStorage.setItem(BADGE_DISMISSED_KEY, '1');
          } catch {
            // Not remembered: it comes back on the next visit.
          }
        }}
        className="absolute -top-2 -right-2 flex size-6 items-center justify-center rounded-full border border-white/20 bg-[oklch(0.17_0.03_266)] text-white/70 shadow-lg hover:text-white"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
